import type { Ref, SelectHTMLAttributes } from 'react';
import { ArrowDown2 } from 'iconsax-reactjs';
import { cx } from '../internal/cx';
import { useFieldProps } from './fieldContext';
import type { ControlSize } from './TextInput';
import styles from './Form.module.css';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options?: SelectOption[];
  /** Adds an empty first option with this text. */
  placeholder?: string;
  size?: ControlSize;
  invalid?: boolean;
  wrapperClassName?: string;
  ref?: Ref<HTMLSelectElement>;
}

/** Styled native <select>: the most robust, accessible choice for single selection. */
export function Select({
  options,
  placeholder,
  size = 'md',
  invalid,
  wrapperClassName,
  className,
  id,
  required,
  disabled,
  children,
  'aria-describedby': ariaDescribedBy,
  ref,
  ...rest
}: SelectProps) {
  const field = useFieldProps({
    id,
    'aria-describedby': ariaDescribedBy,
    invalid,
    required,
    disabled,
  });
  return (
    <div
      className={cx(
        styles.control,
        styles[`control-${size}`],
        styles.selectControl,
        field.invalid && styles.controlInvalid,
        field.disabled && styles.controlDisabled,
        wrapperClassName,
      )}
    >
      <select
        ref={ref}
        id={field.id}
        className={cx(styles.select, className)}
        aria-describedby={field.describedBy}
        aria-invalid={field.invalid || undefined}
        required={field.required || undefined}
        disabled={field.disabled || undefined}
        {...rest}
      >
        {placeholder !== undefined && (
          <option value="" disabled={field.required}>
            {placeholder}
          </option>
        )}
        {options?.map((option) => (
          <option key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </option>
        ))}
        {children}
      </select>
      <span className={styles.selectChevron} aria-hidden="true">
        <ArrowDown2 size={16} color="currentColor" />
      </span>
    </div>
  );
}
